<script setup>
/**
 * SEE IT IN A CHAT — a finished task read as the conversation it becomes, before anything
 * is created.
 *
 * ── WHY THIS IS THE FIRST THING ON THE FIRST SCREEN ───────────────────────────────────
 * A shop owner cannot judge a taxonomy. Asked to choose between "Ask", "Choose" and
 * "Choose from your system" he is being asked to hold our data model in his head before he
 * has decided what he wants. He CAN judge a conversation — he has read ten thousand of them
 * on the same phone — so the choice is made on the thing he is fluent in and the graph is
 * shown to him afterwards, as a receipt.
 *
 * Nothing is created by looking. The starter becomes a draft only when he presses Use this.
 *
 * ── THE ONE THING IT MUST NOT PROMISE ─────────────────────────────────────────────────
 * Inside a chat the assistant phrases every step ITSELF, from the step's `instruction`, in
 * whatever language the customer wrote in. Only a form CARD sends `questionEn`/`questionAr`
 * untouched. So a preview that reads as "it will say exactly this" is the same defect as a
 * stale hint, on screen one — and it is the very distinction the Arabic screen spends a
 * paragraph explaining. `STARTER.WORDING` says so under the thread, permanently, rather
 * than leaving the owner to infer a guarantee the engine never made.
 *
 * ── ANATOMY, borrowed from `TestThread.vue` rather than re-invented ───────────────────
 * The customer's line is a filled bubble at the end of the row — he wrote it, he knows what
 * it says. The assistant's is an outlined bubble at the start, with the taps under it drawn
 * the way `MenuPreview` draws them, and the margin note under that. `dir` sits on the
 * THREAD (so the whole mock mirrors when you read it in Arabic) and `dir="auto"` on every
 * bubble (so one English line inside an Arabic thread is not laid out backwards).
 *
 * It renders what it is given: no fetch, no store, no overlay. `Dialog`, `Popover`,
 * `SidePanel` and `EmptyStateLayout` all reach `TeleportWithDirection` →
 * `useMapGetter('accounts/isRTL')`, which throws inside a render effect AFTER a test has
 * resolved — a failure that can only ever show up as an exit code, and did, on both
 * environments. There is none of that here.
 *
 * Colour only through `n-*` tokens (waslo-lint R1-R4); direction only through logical
 * utilities and the `dir` on the thread (R6).
 */
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';

import { useWasloI18n } from '../../i18n.js';
import { STARTER_IDS, starterById } from '../../helpers/task-starters.js';

const props = defineProps({
  /**
   * Which starter to play.
   *
   * Defaulted rather than required: the render harness mounts every component with no
   * props at all, and a preview that renders nothing there is a preview nobody can judge
   * in both themes. There is always a first starter.
   */
  starterId: { type: String, default: STARTER_IDS[0] },
  /** The language to open in. Empty follows the dashboard's own. */
  initialLang: {
    type: String,
    default: '',
    validator: value => ['', 'en', 'ar'].includes(value),
  },
  /** Reveal the thread one line at a time on mount, instead of showing it whole. */
  autoplay: { type: Boolean, default: false },
});

const emit = defineEmits(['use']);

const { t, locale } = useWasloI18n();

const starter = computed(() => starterById(props.starterId));

const script = computed(() =>
  Array.isArray(starter.value?.script) ? starter.value.script : []
);

const lang = ref(
  props.initialLang ||
    (String(locale.value ?? '').startsWith('ar') ? 'ar' : 'en')
);

const pick = (row, field) =>
  (lang.value === 'ar' ? row?.[`${field}Ar`] : row?.[`${field}En`]) ?? '';

const title = computed(() =>
  lang.value === 'ar' ? starter.value?.titleAr : starter.value?.titleEn
);

// ── the replay ────────────────────────────────────────────────────────────────────────
// The thread starts WHOLE. A staged reveal is what "play" is for, and a component whose
// resting state is half-drawn is one that screenshots differently every run and races
// every mount test.

const shown = ref(script.value.length);

/** One line every 700ms — slow enough to read a bubble, quick enough not to be a wait. */
const BEAT_MS = 700;
let beat = null;

const stop = () => {
  if (beat !== null) {
    clearTimeout(beat);
    beat = null;
  }
};

const step = () => {
  beat = null;
  if (shown.value >= script.value.length) return;
  shown.value += 1;
  beat = setTimeout(step, BEAT_MS);
};

const play = () => {
  stop();
  shown.value = 0;
  beat = setTimeout(step, 0);
};

// A timer that outlives its component is the exact shape of the unhandled callback that
// fails a run by exit code while every assertion passes.
onBeforeUnmount(stop);

onMounted(() => {
  if (props.autoplay && script.value.length > 0) play();
});

// A different starter is a different conversation; show it whole rather than leaving the
// previous one's reveal count on it.
watch(
  () => props.starterId,
  () => {
    stop();
    shown.value = script.value.length;
  }
);

const visible = computed(() =>
  script.value.slice(0, shown.value).map((row, index) => ({
    key: `${props.starterId}-${index}`,
    speaker: row.speaker,
    text: pick(row, 'text'),
    note: pick(row, 'note'),
    options: (Array.isArray(row.options) ? row.options : []).map(option => ({
      id: option.id,
      label: lang.value === 'ar' ? option.labelAr : option.labelEn,
    })),
  }))
);

const toggleClass = active =>
  active
    ? 'bg-n-solid-3 text-n-slate-12'
    : 'text-n-slate-11 hover:bg-n-alpha-2';
</script>

<template>
  <!-- Capped, because a conversation is a column: at full page width the customer's
       bubbles are thrown so far from the assistant's that the thread stops reading as
       one. The parent may still make it narrower. -->
  <section
    class="flex flex-col gap-3 max-w-[44rem]"
    data-waslo-starter-chat
    :data-starter="starterId"
    :data-lang="lang"
  >
    <header class="flex flex-wrap items-center gap-3">
      <h3 class="mb-0 text-base font-medium grow text-n-slate-12" dir="auto">
        <bdi>{{ title }}</bdi>
      </h3>
      <div
        class="flex items-center gap-1 p-1 rounded-lg outline outline-1 outline-n-weak"
        role="group"
        :aria-label="t('WASLO.TASKS.COMPOSE.STARTER.LANGUAGE')"
      >
        <button
          type="button"
          class="px-3 py-1 text-sm rounded-md"
          :class="toggleClass(lang === 'en')"
          :aria-pressed="lang === 'en'"
          data-waslo-starter-en
          @click="lang = 'en'"
        >
          {{ t('WASLO.TASKS.COMPOSE.STARTER.ENGLISH') }}
        </button>
        <button
          type="button"
          class="px-3 py-1 text-sm rounded-md"
          :class="toggleClass(lang === 'ar')"
          :aria-pressed="lang === 'ar'"
          data-waslo-starter-ar
          @click="lang = 'ar'"
        >
          {{ t('WASLO.TASKS.COMPOSE.STARTER.ARABIC') }}
        </button>
      </div>
    </header>

    <!-- The thread. `dir` here, so the bubbles, the taps and the notes all mirror. -->
    <ol
      v-if="visible.length"
      :dir="lang === 'ar' ? 'rtl' : 'ltr'"
      class="flex flex-col gap-4 p-4 mb-0 list-none rounded-2xl bg-n-alpha-1"
      data-waslo-starter-thread
    >
      <li
        v-for="row in visible"
        :key="row.key"
        class="flex flex-col gap-2"
        data-waslo-starter-row
        :data-speaker="row.speaker"
      >
        <!-- The customer: the smaller half, filled, at the end of the row. -->
        <div v-if="row.speaker === 'customer'" class="flex justify-end">
          <p
            dir="auto"
            class="px-4 py-2 mb-0 text-sm leading-6 rounded-2xl max-w-[28rem] bg-n-solid-3 text-n-slate-12"
            data-waslo-starter-text
          >
            <bdi>{{ row.text }}</bdi>
          </p>
        </div>

        <!-- The assistant: the half being judged, so the half with the structure. -->
        <div v-else class="flex items-start gap-3">
          <span
            class="grid mt-1 place-items-center rounded-xl shrink-0 size-8 bg-n-blue-3"
            aria-hidden="true"
          >
            <span class="size-4 text-n-blue-10 i-lucide-bot-message-square" />
          </span>
          <div class="flex flex-col min-w-0 gap-2">
            <p
              dir="auto"
              class="px-4 py-2 mb-0 text-sm leading-6 outline outline-1 rounded-2xl max-w-[28rem] outline-n-weak bg-n-solid-1 text-n-slate-12"
              data-waslo-starter-text
            >
              <bdi>{{ row.text }}</bdi>
            </p>

            <!-- The taps, drawn the way MenuPreview draws them. -->
            <div v-if="row.options.length" class="flex flex-wrap gap-2">
              <span
                v-for="option in row.options"
                :key="option.id"
                dir="auto"
                class="px-3 py-1.5 text-sm font-medium rounded-full outline outline-1 outline-n-weak bg-n-solid-1 text-n-blue-11"
                data-waslo-starter-option
              >
                <bdi>{{ option.label }}</bdi>
              </span>
            </div>

            <p
              v-if="row.note"
              dir="auto"
              class="flex items-start gap-2 mb-0 text-xs leading-5 text-n-slate-10"
              data-waslo-starter-note
            >
              <span
                class="mt-0.5 shrink-0 size-3.5 i-lucide-corner-down-right"
                aria-hidden="true"
              />
              <bdi>{{ row.note }}</bdi>
            </p>
          </div>
        </div>
      </li>
    </ol>

    <p v-else class="mb-0 text-sm text-n-slate-11" data-waslo-starter-empty>
      {{ t('WASLO.TASKS.COMPOSE.STARTER.EMPTY') }}
    </p>

    <!-- THE HONEST LINE, under the thread and not behind a disclosure. -->
    <p
      class="mb-0 text-xs leading-5 max-w-[68ch] text-n-slate-10"
      data-waslo-starter-wording
    >
      {{ t('WASLO.TASKS.COMPOSE.STARTER.WORDING') }}
    </p>

    <footer class="flex flex-wrap items-center gap-2">
      <button
        v-if="script.length"
        type="button"
        class="inline-flex items-center h-8 gap-2 px-3 text-sm font-medium rounded-lg outline outline-1 outline-n-weak text-n-slate-11 hover:bg-n-alpha-1 hover:text-n-slate-12"
        data-waslo-starter-play
        @click="play"
      >
        <span class="size-4 i-lucide-play" aria-hidden="true" />
        {{ t('WASLO.TASKS.COMPOSE.STARTER.PLAY_AGAIN') }}
      </button>
      <button
        type="button"
        class="inline-flex items-center h-8 px-3 text-sm font-medium rounded-lg bg-n-blue-9 text-n-slate-1 hover:bg-n-blue-10"
        data-waslo-starter-use
        @click="emit('use', starterId)"
      >
        {{ t('WASLO.TASKS.COMPOSE.STARTER.USE_THIS') }}
      </button>
    </footer>
  </section>
</template>
